import { Box, Typography, useTheme, lighten, darken } from '@mui/material'
import { useTimelineStyles } from '~/crop/crop-schedule/hooks/use-timeline-styles'
import { useOrgHoverContext, useOrgContextDispatch, setHoveredTask } from '~/common/context/organization/org-context'
import { useTasksContext, useTasksContextDispatch, setSelectedTask } from '../context/tasks-context'

/**
 * A single task bar rendered in the calendar grid.
 * Spans startCol through startCol+span-1 within a CSS grid week row.
 */
export const TaskCalendarTaskBar = ({ task, startCol, span }) => {
  const theme = useTheme()
  const { getTaskStyle } = useTimelineStyles()
  const { hoveredTask } = useOrgHoverContext()
  const orgDispatch = useOrgContextDispatch()
  const { selectedTaskId } = useTasksContext()
  const tasksDispatch = useTasksContextDispatch()

  const backgroundEffect = theme.palette.mode === 'dark' ? darken : lighten
  const taskStyle = getTaskStyle(task.task_types.code)
  const isCompleted = task.state === 'completed'

  const isHovered = hoveredTask === task.id
  const isSelected = selectedTaskId === task.id

  const borderWidth = isSelected ? '2px' : '1px'
  const borderColor = (isHovered || isSelected) ? theme.palette.primary.main : 'transparent'
  // Completed tasks get a much weaker fill
  const fillAmount = isCompleted ? 0.88 : 0.65

  return (
    <Box
      onClick={() => tasksDispatch(setSelectedTask({ taskId: task.id }))}
      onMouseEnter={() => orgDispatch(setHoveredTask({ hoveredTask: task.id }))}
      onMouseLeave={() => orgDispatch(setHoveredTask({ hoveredTask: null }))}
      sx={{
        gridColumn: `${startCol} / span ${span}`,
        backgroundColor: backgroundEffect(taskStyle.color, fillAmount),
        border: `${borderWidth} solid ${borderColor}`,
        borderRadius: '3px',
        px: 0.5,
        py: '1px',
        mb: '2px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0.5,
        overflow: 'hidden',
        transition: 'border-color 150ms ease-in-out, border-width 150ms ease-in-out',
        minHeight: '18px',
      }}
    >
      <Typography
        variant="caption"
        noWrap
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          lineHeight: 1.2,
          userSelect: 'none',
          textDecoration: isCompleted ? 'line-through' : 'none',
          opacity: isCompleted ? 0.6 : 1,
        }}
      >
        {taskStyle.icon} {task.task_types.name}
      </Typography>
      {isCompleted && (
        <Typography variant="caption" sx={{ fontSize: '0.6rem', flexShrink: 0, opacity: 0.7 }}>
          ✓
        </Typography>
      )}
    </Box>
  )
}
